import React, { useEffect, useRef, useState, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import {
  TooltipComponent,
  GridComponent,
  DataZoomComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import axios from 'axios';
import { FallingLines } from 'react-loader-spinner';
import { useAppContext } from '../../../providers/AppProvider';
import dayjs from 'dayjs';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  DataZoomComponent
]);

const SparkTesterChart = ({
  bucket,
  machineName,
  sparkKv,
  sparkPieces,
  startTime,
  endTime
}) => {
  const chartRef = useRef(null);
  const [data, setData] = useState({ sparkKvChanges: [], sparkEvents: [] });
  const [loading, setLoading] = useState(true);
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/spark-events?start=${startTime}&end=${endTime}&bucket=${bucket}&sparkKv=${sparkKv}&sparkPieces=${sparkPieces}`
      );

      const sparkKvChanges = response.data['SparkActualKv Change'] || [];
      const sparkEvents = response.data['SparkPieces Spike'] || [];

      setData({ sparkKvChanges, sparkEvents });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, 90000) : null; // Fetch every 1.5 minutes
    return () => clearInterval(intervalId);
  }, [machineName, startTime, endTime]);

  if (loading) {
    return (
      <Card className="h-100">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  // Define colors
  const greenColor = '#28a745'; // Green for SparkActualKv > 0
  const redColor = '#dc3545'; // Red for SparkActualKv = 0
  const primaryColor = getThemeColor('primary'); // Theme-based primary color for spikes

  // Generate the SparkActualKv Change line
  const sparkKvSeries = data.sparkKvChanges
    .filter(([start_time, end_time, value]) => value > 0)
    .map(([start_time, end_time, value]) => ({
      name: 'SparkActualKv Change',
      type: 'line',
      data: [
        [start_time, value], // Start point
        [end_time, value] // End point
      ],
      lineStyle: { color: value > 0 ? greenColor : redColor, width: 2 },
      showSymbol: false
    }));

  // Generate Spark Spikes
  const sparkSpikes = data.sparkEvents.map(([time, value]) => ({
    name: 'SparkPieces Spike',
    type: 'line',
    data: [
      [time, 0], // Start at 0
      [time, 8] // Spike height
    ],
    lineStyle: { color: primaryColor, width: 2 },
    showSymbol: false
  }));

  const chartOptions = {
    title: { text: 'Spark Tester Chart' },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      formatter: params => {
        const time = params[0].value[0];
        const value = params[0].value[1];
        return `Time: ${dayjs(time).format(
          'YYYY-MM-DD HH:mm:ss'
        )}<br/>Voltage: ${value}`;
      }
    },
    xAxis: { type: 'time', boundaryGap: false },
    yAxis: {
      position: 'right',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    grid: {
      right: 20,
      left: 20,
      bottom: '20px',
      top: '5%',
      containLabel: true
    },
    dataZoom: [{ type: 'inside', xAxisIndex: 0, start: 0, end: 100 }],
    series: [...sparkKvSeries, ...sparkSpikes] // Add all series
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={chartOptions}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  }
};

export default memo(SparkTesterChart);
